import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import { SpreedlyIframeStyles, useSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import { IFrame, TokenExIframeStyles, useTokenExFrame } from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadSpreedlyConfig from "@insite/client-framework/Store/Context/Handlers/LoadSpreedlyConfig";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canApplyPromotionsToCart,
    canPlaceOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import resetCurrentCartId from "@insite/client-framework/Store/Data/Carts/Handlers/ResetCurrentCartId";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import placeOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrder";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import setOnePageCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetOnePageCheckoutPaymentMethod";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import { PaymentMethodDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormHandlers from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormHandlers";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import usePaymentDetailsFormValidation from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormValidation";
import CreditCardBillingAddressEntry, {
    CreditCardBillingAddressEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
    CreditCardDetailsValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import ECheckDetailsEntry, {
    ECheckDetailsEntryStyles,
    Validatable,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ECheckDetailsEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import SavedPaymentProfileEntry, {
    SavedPaymentProfileEntryStyles,
    SavedPaymentProfileValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { generateSpreedlyFrameStyleConfig } from "@insite/mobius/SpreedlyFrame";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { generateTokenExFrameStyleConfig } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cart,
        orderState: getOrderState(state, cart?.orderNumber),
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
        language: state.context.session.language,

        shipTo: getShipToState(state, cart?.shipToId)?.value,
    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
    preloadOrderConfirmationData,
    resetCurrentCartId,
    setOnePageCheckoutPaymentMethod,
    signOut,
    setPaymentDetailsState,
    placeOrder,
    setIsPlacingOrder,
    applyPromotion,
    removePromotion,
    loadSpreedlyConfig,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasToasterContext;

export interface OnePageCheckoutOrderInformationStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    divider?: InjectableCss;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: GridItemProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    poNumberStyle?: SmallHeadingAndTextStyles;
    statusStyle?: SmallHeadingAndTextStyles;
    orderNumberHeading?: TypographyProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    shippingInformationGridItem?: GridItemProps;
    shipToGridItem?: GridItemProps;
    shipToTitleText?: TypographyPresentationProps;
    shipToAddress?: AddressInfoDisplayStyles;
    pickUpTitleText?: TypographyPresentationProps;
    pickUpText?: TypographyPresentationProps;
    pickUpAddress?: AddressInfoDisplayStyles;
    pickUpGridItem?: GridItemProps;
    billToGridItem?: GridItemProps;
    billToTitleText?: TypographyPresentationProps;
    billToText?: TypographyPresentationProps;

    shippingInformationGridContainer?: GridContainerProps;
    shippingInformationTitleGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    shippingInformationAddressGridItem?: GridItemProps;
    shippingAddressTitle?: TypographyProps;
    shippingAddress?: AddressInfoDisplayStyles;
    shippingOtherInformationGridItem?: GridItemProps;
    shippingOtherInformationGridContainer?: GridContainerProps;
    shippingInformationCarrierGridItem?: GridItemProps;
    shippingCarrierTitle?: TypographyProps;
    shippingCarrierDescription?: TypographyProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    shippingDateRequestedTitle?: TypographyProps;
    shippingDateRequestedText?: TypographyProps;
    shippingInformationServiceGridItem?: GridItemProps;
    shippingServiceTitle?: TypographyProps;
    shippingServiceDescription?: TypographyProps;
    shippingInformationPoNumberGridItem?: GridItemProps;
    shippingPoNumberTitle?: TypographyProps;
    shippingPoNumberText?: TypographyProps;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    orderNotesGridItem?: GridItemProps;
    notesDescription?: TypographyPresentationProps;
}

export const OnePageCheckoutOrderInformationStyles: OnePageCheckoutOrderInformationStyles = {
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    shippingInformationServiceGridItem: {
        css: css`
            flex-direction: column;
            max-width: 30% !important;
            flex-basis: 27% !important;
        `,
        width: 3,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingServiceTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
        css: css`
            width: calc(100% + 20px) !important;
            flex-wrap: wrap;
        `,
    },
    shipToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shipToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    billToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    billToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderInformationGridItems: {
        css: css`
            margin-left: 22px !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: unset !important;
                    `,
                    css``,
                    css`
                        padding-left: 0px;
                    `,
                    css``,
                    css``,
                ])}
            @media print {
                padding: 5px !important;
            }
        `,
    },
    orderDetailsGridContainer: {
        css: css`
            // padding-left: 5px;
            padding-bottom: 15px;
            div[data-test-selector^="onePageCheckOutConfirmationBillingInformation"] {
                margin-left: 10px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: unset !important;
                            width: 100%;
                            max-width: 100% !important;
                            margin-left: 20px !important;
                        `,
                        css``,
                        css`
                            padding-left: 0px;
                        `,
                        css``,
                        css``,
                    ])}
            }

            div[data-test-selector="onePageCheckOutConfirmationService"] {
                margin-left: 21 !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: unset !important;

                            margin-left: 45px !important;
                        `,
                        css``,
                        css`
                            padding-left: 0px;
                        `,
                        css``,
                        css``,
                    ])}
            }
            div[data-test-selector="onePageCheckOutConfirmationShippingInformation"] {
                margin-left: 20px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: unset !important;
                            width: 100% !important;
                            max-width: 100% !important;
                            margin-left: 32px;
                        `,
                        css``,
                        css`
                            padding-left: 0px;
                        `,
                        css``,
                        css``,
                    ])}
            }

            > div[class="GridOffset"] {
                margin-left: 9px;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h2",
    },
    divider: {
        css: css`
            padding: 15px;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoGridItem: {
        width: [12, 12, 12, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    // orderNumberText: { variant: "h5" },
    pickUpGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    buttonsGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: flex-end;
        `,
    },

    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    poNumberStyle: {
        heading: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    statusStyle: {
        heading: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    shippingInformationGridContainer: {
        gap: 10,
        css: css`
            margin-left: 32px;
        `,
    },
    shippingInformationTitleGridItem: {
        width: 12,
    },
    shippingInformationTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
            }
            padding-top: 0;
            margin-bottom: 0;
        `,
    },
    shippingInformationAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            max-width: 30% !important;
        `,
    },
    shippingAddressTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingOtherInformationGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    shippingOtherInformationGridContainer: {
        gap: 10,
    },
    shippingInformationCarrierGridItem: {
        css: css`
            flex-direction: column;
            max-width: 27% !important;
            margin-left: 23px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: unset !important;
                    `,
                    css``,
                    css`
                        max-width: 42% !important;
                        padding-left: 0px;
                    `,
                    css``,
                    css``,
                ])}
            div[data-test-selector="onePageCheckOutConfirmationBillingInformation"] {
                border: 1px solid red;
            }

            > div[class="GridOffset"] {
                margin-left: 9px;
                border: 1px solid red;
                div[data-test-selector="onePageCheckOutConfirmationBillingInformation"] {
                    margin-left: 0 !important;
                }
            }
        `,
        width: 6,
    },
    shippingCarrierTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingInformationDateRequestedGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    shippingDateRequestedTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },

    shippingInformationPoNumberGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 6,
    },
    shippingPoNumberTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
};

let tokenExAccountNumberIframe: IFrame | undefined;
let tokenExFrameStyleConfig: TokenExIframeStyles;
let spreedlyFrameStyleConfig: SpreedlyIframeStyles;

const styles = OnePageCheckoutOrderInformationStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const OnePageCheckoutOrderInformation = ({ cart, billTo, orderState, language, shipTo }: Props) => {
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.orderInfoGridItem}>
                <Typography {...styles.orderNumberHeading}>
                    {`${translate("Order")} #`}
                    <Typography {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                        {cart?.erpOrderNumber || cart?.orderNumber}
                    </Typography>
                </Typography>
            </GridItem>
            <GridContainer {...styles.orderDetailsGridContainer}>
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Order Date")}
                        text={getLocalizedDateTime({
                            dateTime: new Date(cart?.orderDate as Date),
                            language,
                        })}
                    />
                </GridItem>
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.poNumberGridItem)}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("PO Number")}
                        text={cart?.poNumber}
                    />
                </GridItem>
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.statusGridItem)}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Status")}
                        text={order?.statusDisplay || cart?.statusDisplay}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.orderDetailsGridContainer}>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Carrier")}
                            text={cart.carrier!.description}
                        />
                    </GridItem>
                )}
                {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                    <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                        <Typography {...styles.shippingDateRequestedTitle}>{translate("Date Requested")}</Typography>
                        <Typography
                            {...styles.shippingDateRequestedText}
                            data-test-selector="orderConfirmation_requestedDate"
                        >
                            <LocalizedDateTime
                                dateTime={cart.requestedDeliveryDateDisplay || cart.requestedPickupDateDisplay}
                            />
                        </Typography>
                    </GridItem>
                )}
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <GridItem
                        {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}
                        data-test-selector="onePageCheckOutConfirmationService"
                    >
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Service")}
                            text={cart.shipVia!.description}
                        />
                    </GridItem>
                )}
                {cart.notes && (
                    <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Details")}
                            text={cart.notes}
                        />
                    </GridItem>
                )}
            </GridContainer>
            <GridContainer {...styles.orderDetailsGridContainer}>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <GridItem
                        {...styles.shippingInformationCarrierGridItem}
                        data-test-selector="onePageCheckOutConfirmationShippingInformation"
                    >
                        <Typography as="h3" {...styles.shippingAddressTitle}>
                            {translate("Shipping Address")}
                        </Typography>
                        <AddressInfoDisplay
                            {...shipTo}
                            state={shipTo.state?.abbreviation}
                            country={shipTo.country?.name}
                        />
                    </GridItem>
                )}
                <GridItem
                    {...styles.shippingInformationCarrierGridItem}
                    data-test-selector="onePageCheckOutConfirmationBillingInformation"
                >
                    <Typography as="h3" {...styles.billToTitleText}>
                        {translate("Billed to")}
                    </Typography>

                    <AddressInfoDisplay {...billTo} state={billTo.state?.abbreviation} country={billTo.country?.name} />
                </GridItem>
            </GridContainer>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withToaster(withHistory(withTheme(OnePageCheckoutOrderInformation)))),
    definition: {
        displayName: "OPC Order Information",
        group: "One Page Checkout",
    },
};

export default widgetModule;
